import { ReactNode, useId, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { formatPriceDisplay } from '../../../../util/formatting';

type CartSectionProps = {
  /** Primary label shown in the header (entity name, property address, or group title). */
  title: string;
  /** Optional second line beneath the title (e.g. entity name beneath property address). */
  subtitle?: string;
  /** When provided, header right rail shows `N Order(s) | Subtotal: $X`. */
  orderCount?: number;
  subtotal?: number;
  /** Optional right-rail CTA rendered between the title and the metadata. */
  rightAction?: ReactNode;
  /** Toggle behaviour. When `expandable` is false the chevron is hidden and the body stays open. */
  defaultExpanded?: boolean;
  expandable?: boolean;
  /** Visual emphasis tier. `outer` is the top-level grouping (On-site / Image Edit), `inner` is per-entity / per-property. */
  variant?: 'outer' | 'inner';
  children: ReactNode;
};

/**
 * Bordered-card collapsible used across the cart and order-confirmation pages.
 * Replaces the legacy `CollapsibleCartSection` / `EntityCartSection` /
 * `PropertyAssetSection` trio, which each rendered the whole header row as a
 * `<button>` and stacked gray bands.
 *
 * Layout: chevron toggle is the only interactive header element, so a real
 * `<button>` inside the header is safe (e.g. an "Add Services" CTA passed via
 * `rightAction`). `useId` keeps `aria-controls` collision-free when two
 * entities or properties share a name.
 */
const CartSection = ({
  title,
  subtitle,
  orderCount,
  subtotal,
  rightAction,
  defaultExpanded = true,
  expandable = true,
  variant = 'inner',
  children,
}: CartSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const bodyId = useId();

  const handleToggle = () => {
    if (expandable) setIsExpanded((prev) => !prev);
  };

  const showMeta = orderCount !== undefined && subtotal !== undefined;
  const orderLabel = orderCount === 1 ? 'Order' : 'Orders';

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background">
      <div
        className={`flex items-center gap-3 px-4 py-3 max-lg:flex-wrap ${
          variant === 'outer' ? 'bg-muted' : 'bg-background'
        } ${expandable ? '' : 'cursor-default'}`}
      >
        {expandable && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleToggle}
            aria-expanded={isExpanded}
            aria-controls={bodyId}
            // h-7 w-7 keeps the established 28px desktop chevron; the
            // primitive's max-lg:h-11/w-11 supplies the 44px touch floor.
            className="h-7 w-7 shrink-0 text-muted-foreground"
          >
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 max-lg:h-6 max-lg:w-6" />
            ) : (
              <ChevronDown className="h-5 w-5 max-lg:h-6 max-lg:w-6" />
            )}
            <span className="sr-only">
              {isExpanded ? 'Collapse' : 'Expand'} {title}
            </span>
          </Button>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <Typography
            as={variant === 'outer' ? 'h2' : 'h3'}
            variant={variant === 'outer' ? 'heading-md' : 'body-semibold'}
            className="truncate"
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              as="p"
              variant="body"
              className="truncate text-muted-foreground"
            >
              {subtitle}
            </Typography>
          )}
        </div>
        {(rightAction || showMeta) && (
          // Below lg this group takes its own full-width row so the CTA and
          // meta rail stop competing with the truncating title for space.
          <div className="flex shrink-0 items-center gap-3 max-lg:min-w-0 max-lg:basis-full max-lg:flex-wrap max-lg:justify-between">
            {rightAction && <div className="shrink-0">{rightAction}</div>}
            {showMeta && (
              <div className="flex shrink-0 items-center gap-2 text-muted-foreground">
                <Typography variant="body">
                  {orderCount} {orderLabel}
                </Typography>
                <span aria-hidden="true" className="text-muted-foreground/60">
                  |
                </span>
                <Typography variant="body">Subtotal:</Typography>
                <Typography variant="body-semibold" className="text-foreground">
                  ${formatPriceDisplay(subtotal)}
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Render the body unconditionally and toggle visibility via `hidden`
          so the toggle button's `aria-controls={bodyId}` always points at a
          live DOM node (ARIA spec requires the controlled element to exist). */}
      <div
        id={bodyId}
        hidden={!isExpanded}
        className="border-t border-border"
      >
        {children}
      </div>
    </div>
  );
};

export default CartSection;
